import { createFileRoute, Link } from "@tanstack/react-router"
import { format } from "date-fns"
import {
	Activity,
	ArrowLeft,
	ExternalLink,
	FileText,
	Gavel,
	KeyRound,
	MessageSquareText,
} from "lucide-react"
import {
	DetailPanel,
	DetailPanelSection,
	PageHeader,
} from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
	DataApplicationAnswers,
	listedPageUrls,
} from "@/features/api-data-access/DataApplicationAnswers"
import { ListingProposalStatusBadge } from "@/features/integration-app-listing-proposals/ListingProposalStatusBadge"
import {
	PermissionRequestDecisionForm,
	PermissionRequestDecisionSummary,
} from "@/features/integration-app-scope-requests/PermissionRequestDecision"
import { PermissionRequestMeasured } from "@/features/integration-app-scope-requests/PermissionRequestMeasured"
import { ScopeRequestPermissions } from "@/features/integration-app-scope-requests/ScopeRequestPermissions"
import { usePermissionRequest } from "@/queries/marketplace"

export const Route = createFileRoute(
	"/integration-apps/permission-requests/$requestId",
)({
	component: PermissionRequestPage,
})

function BackLink() {
	return (
		<Link
			to="/integration-apps/permission-requests"
			search={{} as never}
			className="inline-flex w-fit items-center gap-1 text-meta text-xs hover:text-foreground"
		>
			<ArrowLeft className="h-3.5 w-3.5" />
			Permission requests
		</Link>
	)
}

function formatDateTime(value: string): string {
	return format(new Date(value), "MMM d, yyyy 'at' h:mm a")
}

/**
 * Review of one permission request, from an app owner or an account: the
 * application and the scope changes on the left, what CardNexus measured and
 * the decision on the right (stacked below lg).
 */
function PermissionRequestPage() {
	const { requestId } = Route.useParams()
	const { data, isLoading, error } = usePermissionRequest(requestId)

	if (isLoading) {
		return (
			<div className="flex flex-col gap-4 p-4 sm:p-6">
				<Skeleton className="h-8 w-72 max-w-full" />
				<Skeleton className="h-64 w-full" />
			</div>
		)
	}

	if (error || !data) {
		return (
			<div className="flex flex-col gap-4 p-4 sm:p-6">
				<BackLink />
				<div className="rounded-lg bg-bad-soft px-4 py-3 text-bad">
					{error?.message ?? "Permission request not found"}
				</div>
			</div>
		)
	}

	const { request, measured } = data
	const isPending = request.status === "pending"
	const application = request.application
	const submitter = request.kind === "app" ? request.submittedBy : request.user
	const submitterName = submitter?.username
		? `@${submitter.username}`
		: "a deleted account"

	return (
		<div key={request.id} className="flex flex-col gap-6 p-4 sm:p-6">
			<div className="flex flex-col gap-3">
				<BackLink />
				<PageHeader
					title={
						<span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
							Permission request
							<ListingProposalStatusBadge
								status={request.status}
								className="font-normal text-sm"
							/>
						</span>
					}
					description={
						<>
							Submitted by{" "}
							{submitter ? (
								<Link
									to="/users/$userId"
									params={{ userId: submitter.id }}
									className="text-foreground hover:text-primary-text hover:underline"
								>
									{submitterName}
								</Link>
							) : (
								submitterName
							)}{" "}
							on{" "}
							<span className="tabular-nums">
								{formatDateTime(request.submittedAt)}
							</span>
						</>
					}
					actions={
						request.kind === "app" ? (
							<Button variant="outline" asChild>
								<Link
									to="/integration-apps/$appId"
									params={{ appId: request.app.id }}
								>
									<ExternalLink className="h-4 w-4" />
									Open the app's admin page
								</Link>
							</Button>
						) : (
							<Button variant="outline" asChild>
								<Link
									to="/users/$userId"
									params={{ userId: request.user.id }}
									search={{ tab: "api-usage" }}
								>
									<ExternalLink className="h-4 w-4" />
									Open the account's admin page
								</Link>
							</Button>
						)
					}
				/>
			</div>

			<div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:gap-8">
				<div className="flex min-w-0 flex-col gap-4">
					{application && (
						<DetailPanel title="Application" icon={FileText}>
							<DataApplicationAnswers application={application} />
						</DetailPanel>
					)}

					{request.kind === "app" && request.justification && (
						<DetailPanel
							title="Why the owner needs the scopes"
							icon={MessageSquareText}
						>
							<DetailPanelSection>
								<p className="whitespace-pre-wrap break-words text-base">
									{request.justification}
								</p>
							</DetailPanelSection>
						</DetailPanel>
					)}

					{request.kind === "app" && (
						<DetailPanel
							title="Scopes"
							icon={KeyRound}
							meta={
								isPending
									? "The requested set compared with the app's scopes now. Removing a scope takes it away from every connected user immediately; adding one does not widen the access of people who already connected the app until they connect it again."
									: "The requested set compared with the app's scopes now, not at the time of the decision."
							}
						>
							<ScopeRequestPermissions
								currentScopes={request.currentScopes}
								requestedScopes={request.requestedScopes}
							/>
						</DetailPanel>
					)}
				</div>

				<div className="flex min-w-0 flex-col gap-4 lg:sticky lg:top-4">
					<DetailPanel
						title="Measured"
						icon={Activity}
						meta="From CardNexus data, not from the application"
					>
						<PermissionRequestMeasured
							measured={measured}
							benefits={application?.benefits.benefits ?? []}
							pageUrls={application ? listedPageUrls(application) : []}
						/>
					</DetailPanel>

					<DetailPanel title="Decision" icon={Gavel}>
						<DetailPanelSection>
							{isPending ? (
								<PermissionRequestDecisionForm request={request} />
							) : (
								<PermissionRequestDecisionSummary request={request} />
							)}
						</DetailPanelSection>
					</DetailPanel>
				</div>
			</div>
		</div>
	)
}
